// "My brand" [Mi marca] (M14): restaurant name, contact line, logo, 5 colours, heading and body font, price
// format. Set once in this browser (IndexedDB) and applied to every ad template.
import { create } from 'zustand';
import { createStore, get, set, type UseStore } from 'idb-keyval';
import type { BrandData } from '../model/types';
import { DEFAULT_CURRENCY } from './fields';

export type BrandLogo = { bytes: Uint8Array; type: string; w: number; h: number };
export type BrandProfile = Omit<BrandData, 'logoAsset' | 'logoW' | 'logoH'> & { logo: BrandLogo | null };

export const DEFAULT_BRAND: BrandProfile = {
  name: 'My Restaurant',
  contact: '',
  logo: null,
  colors: {
    primary: [0.784, 0.063, 0.18], // tomato red
    secondary: [1, 0.78, 0.17], // mustard yellow
    accent: [0.106, 0.6, 0.545], // fresh green-teal
    background: [1, 0.973, 0.925], // cream
    text: [0.114, 0.114, 0.114], // near black
  },
  headingFont: 'Montserrat',
  bodyFont: 'Poppins',
  currency: DEFAULT_CURRENCY,
};

let db: UseStore | null = null;
const store = () => (db ??= createStore('epb-brand', 'brand'));
const KEY = 'brand';

type BrandState = { brand: BrandProfile; loaded: boolean; version: number };
export const useBrand = create<BrandState>(() => ({ brand: DEFAULT_BRAND, loaded: false, version: 0 }));

export async function loadBrand(): Promise<BrandProfile> {
  const st = useBrand.getState();
  if (st.loaded) return st.brand;
  const saved = await get<BrandProfile>(KEY, store()).catch(() => undefined);
  const brand = saved
    ? { ...DEFAULT_BRAND, ...saved, colors: { ...DEFAULT_BRAND.colors, ...saved.colors } }
    : DEFAULT_BRAND;
  useBrand.setState({ brand, loaded: true });
  return brand;
}

export async function saveBrand(brand: BrandProfile): Promise<void> {
  useBrand.setState((s) => ({ brand, loaded: true, version: s.version + 1 }));
  await set(KEY, brand, store()).catch(() => undefined);
}

/** Reads an image file as a logo (PNG transparency is kept). */
export async function logoFromFile(file: Blob): Promise<BrandLogo> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const bmp = await createImageBitmap(new Blob([bytes]));
  const logo = { bytes, type: file.type || 'image/png', w: bmp.width, h: bmp.height };
  bmp.close();
  return logo;
}

const hex2 = (v: number) =>
  Math.round(Math.min(1, Math.max(0, v)) * 255)
    .toString(16)
    .padStart(2, '0');
export const rgbHex = (c: [number, number, number]) => '#' + c.map(hex2).join('');
export const hexRgb = (h: string): [number, number, number] =>
  [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16) / 255) as [number, number, number];
